import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot } from '@angular/router';
import { IProduct } from '@models/product';
import { BetaEstimateService } from '@services/beta/beta-estimate.service';
import { DecayBalanceService } from '@services/decay/decay-balance.service';
import { DecayEstimateService } from '@services/decay/decay-estimate.service';
import { ProductService } from '@services/global/product.service';
import { StableBalanceService } from '@services/stable-balance/stable-balance.service';
import { Observable } from 'rxjs';
import Swal from 'sweetalert2';

// tslint:disable-next-line: interface-name
export interface CanComponentDeactivate {
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
}

@Injectable()
export class CanComponentDeactivateGuard implements CanDeactivate<CanComponentDeactivate> {
  constructor(
    private readonly decayService: DecayBalanceService,
    private readonly stableBalanceService: StableBalanceService,
    private readonly betaEstimateService: BetaEstimateService,
    private readonly decayEstimateService: DecayEstimateService,
    private readonly productService: ProductService
  ) {
    this.productService
      .getProducts()
      .toPromise()
      .then(res => {
        this.products = res;
      });
  }

  private products: IProduct[];

  public async canDeactivate(
    component: CanComponentDeactivate,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ) {
    // Checking to see if there is a change in products
    const currentProductId = Number(currentState.url.split('/')[2]);
    const nextStateProductId = Number(nextState.url.split('/')[2]);

    if (!isNaN(currentProductId) && !isNaN(nextStateProductId)) {
      /*
        Last segment is a product id thus we can check to see if it has changed
      */

      if (currentProductId !== nextStateProductId) {
        // product id has changed ask the user if they want to discard
        // const res = await (component.canDeactivate ? component.canDeactivate() : true);

        const decayDeactivate = this.decayService.canDeactivate;
        const stableBalanceDeactivate = this.stableBalanceService.canDeactivate;
        const betaCanDeactivate = this.betaEstimateService.canDeactivate();

        if (betaCanDeactivate && decayDeactivate && stableBalanceDeactivate) {
          // no changes proceed with navigation
          return true;
        } else {
          // prompt the user to discard changes
          const discardChanges = await this.canDeactivatePopupTab(currentProductId);
          if (discardChanges) {
            // TODO discard all changes across all three components
            this.stableBalanceService.discardLocalStorage(currentProductId);
            this.betaEstimateService.discardLocalChanges(currentProductId);
            this.decayEstimateService.clearLocalStorageForProduct(currentProductId);
            return true;
          }
          return false;
        }
      }
      return true;
    } else {
      const decayDeactivate = this.decayService.canDeactivate;
      const stableBalanceDeactivate = this.stableBalanceService.canDeactivate;
      const betaCanDeactivate = this.betaEstimateService.canDeactivate();

      if (betaCanDeactivate && decayDeactivate && stableBalanceDeactivate) {
        // no changes proceed with navigation
        return true;
      } else {
        // prompt the user to discard changes
        const discardChanges = await this.canDeactivatePopupTab(currentProductId);
        if (discardChanges) {
          // TODO discard all changes across all three components
          this.stableBalanceService.discardLocalStorage(currentProductId);
          this.betaEstimateService.discardLocalChanges(currentProductId);
          this.decayEstimateService.clearLocalStorageForProduct(currentProductId);
          return true;
        }
        return false;
      }
    }
  }

  public async canDeactivatePopupTab(productId): Promise<boolean> {
    const promise: Promise<boolean> = new Promise<boolean>((resolve, reject) => {
      Swal.fire({
        title: `${this.products.find(({ ProductId }) => ProductId === productId).ProductName} - Unsaved Changes`,
        text: `Changes have been made to product ${
          this.products.find(({ ProductId }) => ProductId === productId).ProductName
        }\n. Do you want to discard these changes?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Go Back',
        reverseButtons: true,
        allowEscapeKey: true
      })
        .then(result => {
          if (result && result.value) {
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch(err => {
          reject(true);
        });
    });

    return promise;
  }
}
